import { isPlatformHost, isStorefrontPath } from '@/features/storefront/storefrontHost';

/**
 * Google Analytics for NeonCarts' own site - the marketing pages and the
 * merchant dashboard.
 *
 * The same bundle also serves every merchant's shop (a shop subdomain, a custom
 * domain, `/store/...` and `/preview/store/...`). Shoppers' visits are the merchant's business,
 * not ours, so nothing here runs on those pages.
 *
 * Nothing loads until the visitor accepts the cookie banner, and page views
 * carry the path only: URLs such as `/verify-email?email=...` and the reset
 * password link put an address or a token in the query string.
 */

/**
 * The public measurement ID, not a secret. A production build falls back to
 * NeonCarts' own property, the way the platform domain falls back to
 * neoncarts.com; a dev server and the test run send nothing unless it is set.
 */
const EXPLICIT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;
const MEASUREMENT_ID = EXPLICIT_ID ?? (import.meta.env.PROD ? 'G-XCGZLJG710' : '');

const CONSENT_KEY = 'neon_analytics_consent';

export type Consent = 'granted' | 'denied';

type Gtag = (...args: unknown[]) => void;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: Gtag;
  }
}

function isLocalHost(hostname: string): boolean {
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '';
}

/** Whether this page is one NeonCarts measures, consent aside. */
export function analyticsAvailable(
  hostname: string = window.location.hostname,
  pathname: string = window.location.pathname,
): boolean {
  if (!MEASUREMENT_ID) return false;
  if (!isPlatformHost(hostname)) return false;
  // A production build opened locally (`vite preview`) is not a visitor.
  if (isLocalHost(hostname.toLowerCase()) && !EXPLICIT_ID) return false;
  return !isStorefrontPath(pathname);
}

/** The choice made on this page load, which stands even if storage is blocked. */
let sessionConsent: Consent | null = null;

export function getConsent(): Consent | null {
  if (sessionConsent) return sessionConsent;
  try {
    const value = window.localStorage.getItem(CONSENT_KEY);
    return value === 'granted' || value === 'denied' ? value : null;
  } catch {
    // Storage blocked: ask again next visit rather than assume.
    return null;
  }
}

export function setConsent(consent: Consent): void {
  sessionConsent = consent;
  try {
    window.localStorage.setItem(CONSENT_KEY, consent);
  } catch {
    // The choice still applies to this page load.
  }
  if (consent === 'granted') loadGtag();
  else window.gtag?.('consent', 'update', { analytics_storage: 'denied' });
}

let loaded = false;

/** Injects gtag.js once. Only after consent, only where analytics is available. */
export function loadGtag(): void {
  if (loaded || !analyticsAvailable() || getConsent() !== 'granted') return;
  loaded = true;

  window.dataLayer = window.dataLayer ?? [];
  // gtag.js reads the `arguments` object itself, not an array, so this has to
  // be a plain function rather than a rest-parameter arrow.
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer?.push(arguments);
  };
  window.gtag('consent', 'default', {
    analytics_storage: 'granted',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });
  window.gtag('js', new Date());
  // Page views are sent by `trackPageView` on each route change, path only.
  window.gtag('config', MEASUREMENT_ID, { send_page_view: false });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
  document.head.appendChild(script);
}

/** One page view for `pathname`. The query string and hash are never sent. */
export function trackPageView(pathname: string): void {
  if (!loaded || !window.gtag || !analyticsAvailable(window.location.hostname, pathname)) return;
  window.gtag('event', 'page_view', {
    page_path: pathname,
    page_location: `${window.location.origin}${pathname}`,
  });
}

/** A named event, such as `sign_up`. Params must never hold personal data. */
export function trackEvent(name: string, params: Record<string, string | number> = {}): void {
  if (!loaded || !window.gtag || !analyticsAvailable()) return;
  window.gtag('event', name, params);
}

/** Test hook: forget that gtag was loaded. */
export function resetAnalyticsForTests(): void {
  loaded = false;
  sessionConsent = null;
  delete window.gtag;
  delete window.dataLayer;
}
